import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import CalendarView from "@/components/CalendarView";
import ConfirmModal from "@/components/ConfirmModal";
import VariableInserter from "@/components/VariableInserter";
import { CheckCircle, XCircle, UserMinus, CurrencyDollar, ChatCircleDots, Plus, MagnifyingGlass, List, CalendarBlank, CarSimple, Play, FlagCheckered, ArrowsClockwise, X, Wrench, Clock, NotePencil, PaperPlaneRight, Users, Drop, Car as CarIcon, Lightning, Megaphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CUSTOMER_BILLING_ENABLED } from "@/lib/features";
import { useSector } from "@/lib/sectorContext";

const STATUS_CLS = {
  proposed: "bg-slate-100 text-slate-700",
  confirmed: "bg-emerald-50 text-emerald-700",
  checked_in: "bg-amber-50 text-amber-700",
  in_service: "bg-indigo-50 text-indigo-700",
  attended: "bg-slate-900 text-white",
  no_show: "bg-rose-50 text-rose-700",
  paid: "bg-emerald-50 text-emerald-800",
  rescheduled: "bg-amber-50 text-amber-700",
  canceled: "bg-rose-50 text-rose-700",
  done: "bg-slate-100 text-slate-700",
};

const STATUS_LABEL = {
  proposed: "Proposto",
  confirmed: "Confirmado",
  checked_in: "Cliente chegou",
  in_service: "Em atendimento",
  attended: "Finalizado",
  no_show: "Não compareceu",
  paid: "Pago",
  rescheduled: "Reagendado",
  canceled: "Cancelado",
  done: "Concluído",
};

const emptyForm = {
  customer_id: "",
  asset_id: "",
  service_code: "",
  scheduled_at: "",
  duration_min: 60,
  note: "",
};

export default function Appointments() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("list"); // list | calendar
  const [cursor, setCursor] = useState(new Date());
  const [confirmState, setConfirmState] = useState(null); // { path, successMsg, title, message, tone, confirmLabel, loading }
  const [detail, setDetail] = useState(null); // selected appointment for the details modal

  const askConfirm = (path, successMsg, title, message, opts = {}) => {
    setConfirmState({ path, successMsg, title, message, tone: opts.tone || "primary", confirmLabel: opts.confirmLabel || "Confirmar", loading: false });
  };

  const runConfirmed = async () => {
    if (!confirmState) return;
    setConfirmState((s) => ({ ...s, loading: true }));
    try {
      await api.post(confirmState.path);
      toast.success(confirmState.successMsg);
      setConfirmState(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
      setConfirmState((s) => s && { ...s, loading: false });
    }
  };

  const load = async () => {
    const [a, c, s, v] = await Promise.all([
      api.get("/appointments"),
      api.get("/customers"),
      api.get("/services"),
      api.get("/vehicles"),
    ]);
    setRows(a.data);
    setCustomers(c.data);
    setServices(s.data);
    setAssets(v.data);
  };

  useEffect(() => {
    load();
    if (!CUSTOMER_BILLING_ENABLED) return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) {
      toast.info("Verificando pagamento…");
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success("Pagamento confirmado. Agendamento marcado como pago.");
            load();
            return;
          }
          if (data.status === "expired" || attempts >= 6) {
            toast.error("Pagamento não confirmado.");
            return;
          }
          attempts += 1;
          setTimeout(poll, 2500);
        } catch {
          toast.error("Falha ao verificar pagamento.");
        }
      };
      poll();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("canceled")) {
      toast.info("Pagamento cancelado.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const post = async (path, label) => {
    try {
      await api.post(path);
      toast.success(label);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const ensureConversation = async (customerId) => {
    // Reuse an open conversation if one exists; otherwise open one
    // explicitly via the backend so the reminder can land. The previous
    // behaviour ("erro: notifique pela tela de Eventos primeiro") was
    // a dead-end for tenants that just imported their base — they had
    // customers + appointments but ZERO conversations yet.
    const { data } = await api.get("/conversations");
    const found = data.find((c) => c.conversation.customer_id === customerId && c.conversation.status !== "closed");
    if (found) return found.conversation.id;
    const created = await api.post(`/conversations/open-for-customer/${customerId}`);
    return created.data.conversation.id;
  };

  // ── Lembretes — guard de double-click ────────────────────────────
  // Bug Omega (jun/2026): operador clicava "Lembrar" e via 2 envios
  // no mockup. O dedup do backend pega quando os 2 requests caem
  // dentro de 30s, mas a UX correta é desabilitar o botão enquanto
  // o request está em flight. Estado por id (Set) pra que outros
  // botões não bloqueiem.
  const [remindingIds, setRemindingIds] = useState(() => new Set());

  const sendReminder = async (a) => {
    if (remindingIds.has(a.id)) return;  // já está em flight
    setRemindingIds((prev) => new Set(prev).add(a.id));
    try {
      const r = await api.post("/actions/execute", {
        code: "send_reminder",
        confirmed: true,
        params: {
          appointment_id: a.id,
          conversation_id: await ensureConversation(a.customer_id),
          scheduled_at: new Date(a.scheduled_at).toLocaleString("pt-BR"),
        },
      });
      if (r.data?.ok) toast.success("Lembrete enviado.");
      else toast.error(r.data?.reason || "Falha");
    } catch (e) {
      toast.error(e?.message || "Falha ao enviar lembrete");
    } finally {
      setRemindingIds((prev) => {
        const next = new Set(prev);
        next.delete(a.id);
        return next;
      });
    }
  };

  const sendPayment = async (a) => {
    try {
      const r = await api.post("/payments/checkout", {
        appointment_id: a.id,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.open(r.data.url, "_blank");
        toast.success("Stripe Checkout aberto em nova aba.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar cobrança");
    }
  };

  const openNew = () => { setForm(emptyForm); setOpen(true); };

  const openNewWithDate = (date) => {
    // Default to 09:00 on the selected day
    const d = new Date(date);
    d.setHours(9, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setForm({ ...emptyForm, scheduled_at: local });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        asset_id: form.asset_id || null,
        service_code: form.service_code,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_min: Number(form.duration_min) || 60,
        note: form.note || null,
      };
      await api.post("/appointments", payload);
      toast.success("Agendamento criado");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar agendamento");
    } finally {
      setSaving(false);
    }
  };

  const name = (id) => customers.find((c) => c.id === id)?.name || "—";
  const serviceLabel = (code) => services.find((s) => s.code === code)?.name || code;

  // Keep the details modal in sync with the latest row data after any
  // status mutation (`load()` reloads `rows`).
  useEffect(() => {
    if (!detail) return;
    const fresh = rows.find((r) => r.id === detail.id);
    if (!fresh) setDetail(null);
    else if (fresh !== detail) setDetail(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // ── Envio em Massa state (pedido Omega jun/2026) ────────────────
  const [bulkOpen, setBulkOpen] = useState(false);

  // Deep-link: `/app/agendamentos?open=<appointment_id>` (used by the
  // Dashboard "Agenda do dia" widget) auto-opens the details modal.
  useEffect(() => {
    if (!rows.length) return;
    const params = new URLSearchParams(window.location.search);
    const openId = params.get("open");
    if (!openId) return;
    const appt = rows.find((r) => r.id === openId);
    if (appt) {
      setDetail(appt);
      setView("calendar");
      // Clean the URL so a refresh doesn't keep opening the modal.
      params.delete("open");
      const qs = params.toString();
      window.history.replaceState({}, "",
        window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [rows]);
  const customerAssets = useMemo(
    () => (form.customer_id ? assets.filter((a) => a.customer_id === form.customer_id) : []),
    [form.customer_id, assets],
  );

  const filtered = useMemo(() => {
    let list = rows;
    if (filter) list = list.filter((r) => r.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => {
        const cName = name(r.customer_id).toLowerCase();
        const svc = (serviceLabel(r.service_code) || "").toLowerCase();
        const when = new Date(r.scheduled_at).toLocaleString("pt-BR").toLowerCase();
        return cName.includes(q) || svc.includes(q) || when.includes(q) || (r.service_code || "").toLowerCase().includes(q);
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, search, customers, services]);

  return (
    <>
      <Topbar
        title="Agenda"
        subtitle="Agendamentos gerados pela IA no WhatsApp — do agendamento ao comparecimento e pagamento. Se você tem sistema de gestão, esta agenda é complementar à do seu ERP."
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5" role="tablist">
              <button
                onClick={() => setView("list")}
                data-testid="view-list-btn"
                className={`inline-flex items-center gap-1 px-3 py-1 text-[12px] font-medium rounded-full transition-colors ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              >
                <List size={12} weight="bold" /> Lista
              </button>
              <button
                onClick={() => setView("calendar")}
                data-testid="view-calendar-btn"
                className={`inline-flex items-center gap-1 px-3 py-1 text-[12px] font-medium rounded-full transition-colors ${view === "calendar" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              >
                <CalendarBlank size={12} weight="bold" /> Calendário
              </button>
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="appointments-filter"
              className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-[12px] font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 cursor-pointer"
            >
              <option value="">Todos os status</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="appointments-bulk-btn"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors text-[12px] font-medium whitespace-nowrap"
              title="Enviar mensagens em massa por filtro"
            >
              <PaperPlaneRight size={13} weight="bold" /> Envio em massa
            </button>
            <button
              onClick={openNew}
              data-testid="appointments-new-btn"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12px] font-medium shadow-sm whitespace-nowrap"
            >
              <Plus size={13} weight="bold" /> Novo agendamento
            </button>
          </div>
        }
      />
      <div className="p-6 md:p-8 space-y-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por cliente, serviço ou data…"
            data-testid="appointments-search"
            className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm"
          />
        </div>
        {view === "calendar" ? (
          <CalendarView
            appointments={filtered}
            customers={customers}
            services={services}
            cursor={cursor}
            setCursor={setCursor}
            onSelectDay={openNewWithDate}
            onSelectAppointment={(a) => setDetail(a)}
          />
        ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Quando</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Cliente</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Serviço</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-500">Nenhum agendamento.</td></tr>}
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`appointment-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{new Date(r.scheduled_at).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-3 text-slate-700">{name(r.customer_id)}</td>
                  <td className="px-4 py-3 text-slate-700">{serviceLabel(r.service_code)} <span className="text-slate-400 font-mono text-xs">({r.service_code})</span></td>
                  <td className="px-4 py-3"><span className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded ${STATUS_CLS[r.status] || "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {/* Pipeline operacional — 1 botão primário por estado */}
                      {r.status === "proposed" && (
                        <button
                          onClick={() => askConfirm(`/appointments/${r.id}/confirm`, "Agendamento confirmado", "Confirmar agendamento?", `Confirmar ${name(r.customer_id)} em ${new Date(r.scheduled_at).toLocaleString("pt-BR")}?`, { tone: "primary", confirmLabel: "Confirmar" })}
                          data-testid={`appt-confirm-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:bg-emerald-50 px-2.5 py-1 rounded-full font-medium"
                        >
                          <CheckCircle size={14} weight="fill" /> Confirmar
                        </button>
                      )}
                      {r.status === "confirmed" && (
                        <button
                          onClick={() => post(`/appointments/${r.id}/checkin`, "Cliente registrado como chegou")}
                          data-testid={`appt-checkin-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-amber-700 hover:bg-amber-50 px-2.5 py-1 rounded-full font-medium"
                        >
                          <CarSimple size={14} weight="fill" /> Cliente chegou
                        </button>
                      )}
                      {r.status === "checked_in" && (
                        <button
                          onClick={() => post(`/appointments/${r.id}/start_service`, "Atendimento iniciado")}
                          data-testid={`appt-start-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:bg-indigo-50 px-2.5 py-1 rounded-full font-medium"
                        >
                          <Play size={14} weight="fill" /> Iniciar atendimento
                        </button>
                      )}
                      {r.status === "in_service" && (
                        <button
                          onClick={() => askConfirm(`/appointments/${r.id}/finish`, "Atendimento finalizado · follow-up será enviado", "Finalizar atendimento?", `Confirmar que o atendimento de ${name(r.customer_id)} foi concluído? O follow-up automático será enviado de acordo com a configuração de Pós-atendimento.`, { tone: "primary", confirmLabel: "Finalizar" })}
                          data-testid={`appt-finish-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-white bg-slate-900 hover:bg-slate-800 px-2.5 py-1 rounded-full font-medium"
                        >
                          <FlagCheckered size={14} weight="fill" /> Finalizar
                        </button>
                      )}

                      {/* Secundárias — sempre disponíveis até finalização */}
                      {!["attended", "paid", "no_show", "canceled", "done"].includes(r.status) && (
                        <button
                          onClick={() => sendReminder(r)}
                          disabled={remindingIds.has(r.id)}
                          data-testid={`appt-remind-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:bg-slate-100 px-2 py-1 rounded-full disabled:opacity-50 disabled:cursor-wait"
                          title="Enviar lembrete"
                        >
                          {remindingIds.has(r.id) ? (
                            <><ArrowsClockwise size={13} className="animate-spin" /> Enviando…</>
                          ) : (
                            <><ChatCircleDots size={13} /> Lembrar</>
                          )}
                        </button>
                      )}
                      {!["attended", "paid", "no_show", "canceled", "done"].includes(r.status) && (
                        <button
                          onClick={() => askConfirm(`/appointments/${r.id}/no_show`, "Não compareceu · mensagem cordial enviada", "Marcar como não compareceu?", `Registrar que ${name(r.customer_id)} não compareceu? O AgentUS vai enviar uma mensagem cordial oferecendo reagendamento.`, { tone: "warning", confirmLabel: "Não compareceu" })}
                          data-testid={`appt-noshow-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-full"
                          title="Não compareceu"
                        >
                          <UserMinus size={13} /> Não comp.
                        </button>
                      )}
                      {r.status === "no_show" && (
                        <button
                          onClick={() => askConfirm(`/appointments/${r.id}/confirm`, "Reagendado · status restaurado", "Reagendar este atendimento?", `Restaurar o status para 'confirmado'? Você pode editar a data abrindo o agendamento.`, { tone: "primary", confirmLabel: "Reagendar" })}
                          data-testid={`appt-reschedule-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-amber-700 hover:bg-amber-50 px-2.5 py-1 rounded-full font-medium"
                        >
                          <ArrowsClockwise size={13} weight="bold" /> Reagendar
                        </button>
                      )}

                      {/* Financeiro */}
                      {CUSTOMER_BILLING_ENABLED && ["attended", "in_service"].includes(r.status) && (
                        <button
                          onClick={() => sendPayment(r)}
                          data-testid={`appt-charge-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-emerald-800 hover:bg-emerald-50 px-2 py-1 rounded-full"
                          title="Cobrar"
                        >
                          <CurrencyDollar size={13} weight="fill" /> Cobrar
                        </button>
                      )}
                      {CUSTOMER_BILLING_ENABLED && r.status === "attended" && (
                        <button
                          onClick={() => askConfirm(`/appointments/${r.id}/paid`, "Marcado como pago", "Marcar como pago?", `Confirmar pagamento do atendimento de ${name(r.customer_id)}?`, { tone: "primary", confirmLabel: "Marcar pago" })}
                          data-testid={`appt-paid-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-emerald-800 hover:bg-emerald-50 px-2 py-1 rounded-full"
                          title="Marcar pago"
                        >
                          <CheckCircle size={13} weight="fill" /> Pago
                        </button>
                      )}

                      {/* Cancelar — escondido após finalização */}
                      {!["canceled", "paid", "done", "attended"].includes(r.status) && (
                        <button
                          onClick={() => askConfirm(`/appointments/${r.id}/cancel`, "Agendamento cancelado", "Cancelar agendamento?", `Tem certeza que deseja cancelar o agendamento de ${name(r.customer_id)} em ${new Date(r.scheduled_at).toLocaleString("pt-BR")}? Esta ação não pode ser desfeita.`, { tone: "danger", confirmLabel: "Sim, cancelar" })}
                          data-testid={`appt-cancel-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:bg-slate-100 px-2 py-1 rounded-full"
                          title="Cancelar"
                        >
                          <XCircle size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-md border border-slate-200 w-full max-w-md p-6 space-y-4" data-testid="appointment-modal">
            <h3 className="font-heading text-xl font-semibold">Novo agendamento</h3>

            <label className="block">
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Cliente</span>
              <select
                required
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value, asset_id: "" })}
                data-testid="appointment-form-customer"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">Selecione…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} — {c.phone}</option>
                ))}
              </select>
            </label>

            {customerAssets.length > 0 && (
              <label className="block">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Veículo (opcional)</span>
                <select
                  value={form.asset_id}
                  onChange={(e) => setForm({ ...form, asset_id: e.target.value })}
                  data-testid="appointment-form-asset"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
                >
                  <option value="">—</option>
                  {customerAssets.map((v) => (
                    <option key={v.id} value={v.id}>{v.label || `${v.brand || ""} ${v.model || ""}`.trim() || v.identifier}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="block">
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Serviço</span>
              <select
                required
                value={form.service_code}
                onChange={(e) => setForm({ ...form, service_code: e.target.value })}
                data-testid="appointment-form-service"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">Selecione…</option>
                {services.map((s) => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Data e hora</span>
                <input
                  required
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                  data-testid="appointment-form-datetime"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Duração (min)</span>
                <input
                  type="number"
                  min="15"
                  step="15"
                  value={form.duration_min}
                  onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                  data-testid="appointment-form-duration"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Observação</span>
              <textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="appointment-form-note"
                rows="2"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="text-sm text-slate-600">Cancelar</button>
              <button
                type="submit"
                disabled={saving}
                data-testid="appointment-form-submit"
                className="inline-flex items-center px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12px] font-medium shadow-sm disabled:opacity-60"
              >
                {saving ? "Salvando…" : "Criar agendamento"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmModal
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        tone={confirmState?.tone}
        confirmLabel={confirmState?.confirmLabel}
        loading={!!confirmState?.loading}
        onConfirm={runConfirmed}
        onCancel={() => setConfirmState(null)}
      />

      {detail && (() => {
        const c = customers.find((x) => x.id === detail.customer_id);
        const v = assets.find((x) => x.id === detail.asset_id);
        const vehicleLabel = v ? (v.label || `${v.brand || ""} ${v.model || ""}`.trim() || v.identifier) : "—";
        const status = detail.status;
        const statusLabel = STATUS_LABEL[status] || status;
        const statusCls = STATUS_CLS[status] || "bg-slate-100 text-slate-600";
        const isClosed = ["attended", "paid", "no_show", "canceled", "done"].includes(status);
        const close = () => setDetail(null);
        const wrap = (fn) => async (...args) => { await fn(...args); /* load() inside helpers refreshes rows */ };
        return (
          <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={close} data-testid="appointment-detail-modal">
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md border border-slate-200 w-full max-w-lg overflow-hidden">
              <header className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-100">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Agendamento</div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 truncate" data-testid="appointment-detail-customer">{name(detail.customer_id)}</h3>
                  {c?.phone && <p className="text-xs text-slate-500 tabular-nums">{c.phone}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded ${statusCls}`} data-testid="appointment-detail-status">{statusLabel}</span>
                  <button onClick={close} data-testid="appointment-detail-close" className="text-slate-400 hover:text-slate-700 p-1">
                    <X size={16} weight="bold" />
                  </button>
                </div>
              </header>

              <div className="px-6 py-4 space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <CalendarBlank size={14} className="text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Quando</div>
                    <div className="text-slate-900 tabular-nums" data-testid="appointment-detail-when">{new Date(detail.scheduled_at).toLocaleString("pt-BR")}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock size={14} className="text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Duração</div>
                    <div className="text-slate-900 tabular-nums">{detail.duration_min || 60} min</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Wrench size={14} className="text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Serviço</div>
                    <div className="text-slate-900" data-testid="appointment-detail-service">
                      {serviceLabel(detail.service_code)} <span className="text-slate-400 font-mono text-xs">({detail.service_code})</span>
                    </div>
                  </div>
                </div>
                {detail.asset_id && (
                  <div className="flex items-start gap-2">
                    <CarSimple size={14} className="text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Veículo</div>
                      <div className="text-slate-900" data-testid="appointment-detail-vehicle">{vehicleLabel}</div>
                    </div>
                  </div>
                )}
                {detail.note && (
                  <div className="flex items-start gap-2">
                    <NotePencil size={14} className="text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Observação</div>
                      <div className="text-slate-700 whitespace-pre-wrap">{detail.note}</div>
                    </div>
                  </div>
                )}
              </div>

              <footer className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-end gap-2">
                {status === "proposed" && (
                  <button
                    onClick={() => askConfirm(`/appointments/${detail.id}/confirm`, "Agendamento confirmado", "Confirmar agendamento?", `Confirmar ${name(detail.customer_id)} em ${new Date(detail.scheduled_at).toLocaleString("pt-BR")}?`, { tone: "primary", confirmLabel: "Confirmar" })}
                    data-testid="appointment-detail-confirm"
                    className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-white border border-emerald-200 hover:bg-emerald-50 px-3 py-1.5 rounded-full font-medium"
                  >
                    <CheckCircle size={13} weight="fill" /> Confirmar
                  </button>
                )}
                {status === "confirmed" && (
                  <button
                    onClick={wrap(() => post(`/appointments/${detail.id}/checkin`, "Cliente registrado como chegou"))}
                    data-testid="appointment-detail-checkin"
                    className="inline-flex items-center gap-1 text-xs text-amber-700 bg-white border border-amber-200 hover:bg-amber-50 px-3 py-1.5 rounded-full font-medium"
                  >
                    <CarSimple size={13} weight="fill" /> Cliente chegou
                  </button>
                )}
                {status === "checked_in" && (
                  <button
                    onClick={wrap(() => post(`/appointments/${detail.id}/start_service`, "Atendimento iniciado"))}
                    data-testid="appointment-detail-start"
                    className="inline-flex items-center gap-1 text-xs text-indigo-700 bg-white border border-indigo-200 hover:bg-indigo-50 px-3 py-1.5 rounded-full font-medium"
                  >
                    <Play size={13} weight="fill" /> Iniciar atendimento
                  </button>
                )}
                {status === "in_service" && (
                  <button
                    onClick={() => askConfirm(`/appointments/${detail.id}/finish`, "Atendimento finalizado · follow-up será enviado", "Finalizar atendimento?", `Confirmar que o atendimento de ${name(detail.customer_id)} foi concluído? O follow-up automático será enviado.`, { tone: "primary", confirmLabel: "Finalizar" })}
                    data-testid="appointment-detail-finish"
                    className="inline-flex items-center gap-1 text-xs text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5 rounded-full font-medium"
                  >
                    <FlagCheckered size={13} weight="fill" /> Finalizar
                  </button>
                )}
                {!isClosed && (
                  <button
                    onClick={() => sendReminder(detail)}
                    data-testid="appointment-detail-remind"
                    className="inline-flex items-center gap-1 text-xs text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 px-3 py-1.5 rounded-full"
                  >
                    <ChatCircleDots size={13} /> Lembrar
                  </button>
                )}
                {!isClosed && (
                  <button
                    onClick={() => askConfirm(`/appointments/${detail.id}/no_show`, "Não compareceu · mensagem cordial enviada", "Marcar como não compareceu?", `Registrar que ${name(detail.customer_id)} não compareceu? O AgentUS envia uma mensagem cordial oferecendo reagendamento.`, { tone: "warning", confirmLabel: "Não compareceu" })}
                    data-testid="appointment-detail-noshow"
                    className="inline-flex items-center gap-1 text-xs text-rose-700 bg-white border border-rose-200 hover:bg-rose-50 px-3 py-1.5 rounded-full"
                  >
                    <UserMinus size={13} /> Não compareceu
                  </button>
                )}
                {status === "no_show" && (
                  <button
                    onClick={() => askConfirm(`/appointments/${detail.id}/confirm`, "Reagendado · status restaurado", "Reagendar este atendimento?", `Restaurar o status para 'confirmado'?`, { tone: "primary", confirmLabel: "Reagendar" })}
                    data-testid="appointment-detail-reschedule"
                    className="inline-flex items-center gap-1 text-xs text-amber-700 bg-white border border-amber-200 hover:bg-amber-50 px-3 py-1.5 rounded-full font-medium"
                  >
                    <ArrowsClockwise size={13} weight="bold" /> Reagendar
                  </button>
                )}
                {CUSTOMER_BILLING_ENABLED && ["attended", "in_service"].includes(status) && (
                  <button
                    onClick={() => sendPayment(detail)}
                    data-testid="appointment-detail-charge"
                    className="inline-flex items-center gap-1 text-xs text-emerald-800 bg-white border border-emerald-200 hover:bg-emerald-50 px-3 py-1.5 rounded-full"
                  >
                    <CurrencyDollar size={13} weight="fill" /> Cobrar
                  </button>
                )}
                {CUSTOMER_BILLING_ENABLED && status === "attended" && (
                  <button
                    onClick={() => askConfirm(`/appointments/${detail.id}/paid`, "Marcado como pago", "Marcar como pago?", `Confirmar pagamento do atendimento de ${name(detail.customer_id)}?`, { tone: "primary", confirmLabel: "Marcar pago" })}
                    data-testid="appointment-detail-paid"
                    className="inline-flex items-center gap-1 text-xs text-emerald-800 bg-white border border-emerald-200 hover:bg-emerald-50 px-3 py-1.5 rounded-full"
                  >
                    <CheckCircle size={13} weight="fill" /> Marcar pago
                  </button>
                )}
                {!["canceled", "paid", "done", "attended"].includes(status) && (
                  <button
                    onClick={() => askConfirm(`/appointments/${detail.id}/cancel`, "Agendamento cancelado", "Cancelar agendamento?", `Tem certeza que deseja cancelar o agendamento de ${name(detail.customer_id)} em ${new Date(detail.scheduled_at).toLocaleString("pt-BR")}?`, { tone: "danger", confirmLabel: "Sim, cancelar" })}
                    data-testid="appointment-detail-cancel"
                    className="inline-flex items-center gap-1 text-xs text-slate-500 bg-white border border-slate-200 hover:bg-slate-100 px-3 py-1.5 rounded-full"
                  >
                    <XCircle size={13} /> Cancelar
                  </button>
                )}
              </footer>
            </div>
          </div>
        );
      })()}

      {bulkOpen && (
        <BulkSendModal onClose={() => setBulkOpen(false)} />
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Envio em Massa — modal
// ──────────────────────────────────────────────────────────────────────
//
// 5 segmentos rápidos pedidos pela Omega (19/jun/2026):
//   - Ativos: interagiram nos últimos 60 dias
//   - Inativos: sem interação há mais de 60 dias
//   - Próxima troca de óleo: veículo com data prevista nos próximos N dias
//   - Próxima revisão: veículo com data prevista nos próximos N dias
//   - Sem veículo cadastrado
//
// O operador escolhe segmento → vê a prévia dos destinatários (count +
// lista) → escreve a mensagem (com placeholders {nome_cliente}, {veiculo}) →
// dispara. Backend reusa `campaign_engine` por baixo dos panos, então o
// envio aparece em /app/campanhas no histórico.

const BULK_KINDS = [
  { code: "active",      label: "Clientes ativos",         icon: Lightning,
    hint: "Quem interagiu nos últimos 60 dias", needsWindow: false },
  { code: "inactive",    label: "Clientes inativos",       icon: Clock,
    hint: "Sem mensagem há mais de 60 dias",    needsWindow: false },
  { code: "oil_due",     label: "Próxima troca de óleo",   icon: Drop,
    hint: "Veículos com troca prevista no horizonte", needsWindow: true },
  { code: "service_due", label: "Próxima revisão",         icon: Wrench,
    hint: "Veículos com revisão prevista no horizonte", needsWindow: true },
  { code: "no_vehicle",  label: "Sem veículo cadastrado",  icon: CarIcon,
    hint: "Bons candidatos pra puxar o cadastro", needsWindow: false },
];

function BulkSendModal({ onClose }) {
  // v0.99.19 · Fev/2026 — kinds automotivos ficam ocultos em outros verticais.
  const { isAutomotive, labels } = useSector();
  const _assetSing = labels?.assetSingular?.() || "cliente";
  const _serviceSing = labels?.serviceSingular?.() || "atendimento";
  const BULK_KINDS_FILTERED = isAutomotive
    ? BULK_KINDS
    : BULK_KINDS.filter((k) => !["oil_due", "service_due", "no_vehicle"].includes(k.code));
  const [kind, setKind] = useState(BULK_KINDS_FILTERED[0]?.code || "active");
  const [windowDays, setWindowDays] = useState(30);
  const [audience, setAudience] = useState({ total: null, sample: [] });
  const [loadingAudience, setLoadingAudience] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messageRef = useRef(null);

  const meta = BULK_KINDS_FILTERED.find((k) => k.code === kind) || BULK_KINDS_FILTERED[0];

  const messageVars = useMemo(() => {
    const base = [
      { token: "{nome_cliente}", label: "Primeiro nome do cliente" },
      { token: "{nome_completo}", label: "Nome completo" },
    ];
    if (meta.needsWindow) base.push({ token: "{veiculo}", label: "Veículo do cliente" });
    return base;
  }, [meta.needsWindow]);

  const loadAudience = async () => {
    setLoadingAudience(true);
    try {
      const { data } = await api.get("/appointments/bulk-audience", {
        params: { kind, window_days: windowDays },
      });
      setAudience({ total: data.total || 0, sample: data.sample || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar audiência");
      setAudience({ total: 0, sample: [] });
    } finally {
      setLoadingAudience(false);
    }
  };

  useEffect(() => { loadAudience(); /* eslint-disable-next-line */ }, [kind, windowDays]);

  const send = async () => {
    if (sending) return;
    if (!message.trim() || message.trim().length < 5) {
      toast.error("Escreva uma mensagem (mín 5 caracteres)");
      return;
    }
    if (!audience.total) {
      toast.error("Nenhum cliente nesse filtro");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/appointments/bulk-send", {
        kind,
        message_template: message,
        window_days: windowDays,
      });
      toast.success(
        `Disparo iniciado — ${data.sent} enviada${data.sent === 1 ? "" : "s"}` +
        (data.failed ? `, ${data.failed} falha${data.failed === 1 ? "" : "s"}` : "")
      );
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar em massa");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-md flex items-center justify-center p-4"
      onClick={() => !sending && onClose()}
      data-testid="bulk-send-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200/60 w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
        data-testid="bulk-send-modal"
      >
        <header className="px-6 py-4 border-b border-slate-100 flex items-start gap-3">
          <span className="shrink-0 w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
            <Megaphone size={18} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-[17px] font-semibold tracking-tight text-slate-900">Envio em massa</h3>
            <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">
              Escolha um segmento, revise quem vai receber e dispare a mensagem.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !sending && onClose()}
            className="shrink-0 -mt-1 -mr-1 w-8 h-8 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center"
            aria-label="Fechar"
            data-testid="bulk-send-close"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5">
          {/* Segmentos */}
          <aside>
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-2">Segmento</div>
            <div className="space-y-1.5">
              {BULK_KINDS_FILTERED.map((k) => {
                const Icon = k.icon;
                const active = kind === k.code;
                return (
                  <button
                    key={k.code}
                    type="button"
                    onClick={() => setKind(k.code)}
                    data-testid={`bulk-kind-${k.code}`}
                    className={`w-full text-left flex items-start gap-2 px-3 py-2.5 rounded-xl border transition-colors ${
                      active
                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                    }`}
                  >
                    <Icon size={14} weight="fill" className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold">{k.label}</div>
                      <div className={`text-[11px] mt-0.5 leading-snug ${active ? "text-slate-300" : "text-slate-500"}`}>{k.hint}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {meta.needsWindow && (
              <div className="mt-4">
                <div className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5">Janela (dias)</div>
                <select
                  value={windowDays}
                  onChange={(e) => setWindowDays(Number(e.target.value))}
                  data-testid="bulk-window"
                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-slate-900"
                >
                  {[7, 15, 30, 60, 90].map((d) => (
                    <option key={d} value={d}>Próximos {d} dias</option>
                  ))}
                </select>
              </div>
            )}
          </aside>

          {/* Mensagem + preview */}
          <main className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Mensagem</div>
                <div className="text-[10.5px] text-slate-400">{message.length}/1500</div>
              </div>
              <textarea
                ref={messageRef}
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 1500))}
                placeholder={isAutomotive
                  ? "Oi {nome_cliente}! Identificamos que seu veículo {veiculo} está perto da próxima manutenção. Posso confirmar um horário?"
                  : `Oi {nome_cliente}! Vim confirmar seu próximo ${_serviceSing}. Podemos agendar?`}
                rows={6}
                data-testid="bulk-message"
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[13px] bg-white focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
              />
              <div className="mt-1.5">
                <VariableInserter
                  textareaRef={messageRef}
                  value={message}
                  onChange={setMessage}
                  variables={messageVars}
                />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
                  <Users size={13} weight="fill" />
                  {loadingAudience ? "Carregando…" : (
                    audience.total === null ? "Sem prévia" :
                    audience.total === 0 ? "Nenhum cliente nesse filtro" :
                    `${audience.total} ${audience.total === 1 ? "cliente" : "clientes"} vão receber`
                  )}
                </div>
                <button
                  type="button"
                  onClick={loadAudience}
                  className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                  data-testid="bulk-reload"
                >
                  <ArrowsClockwise size={11} weight="bold" /> Atualizar
                </button>
              </div>
              <div className="max-h-44 overflow-y-auto pr-1 space-y-1 chat-scroll">
                {audience.sample.slice(0, 10).map((c) => (
                  <div key={c.customer_id} className="text-[12px] text-slate-700 flex items-center justify-between gap-2" data-testid={`bulk-target-${c.customer_id}`}>
                    <span className="truncate">{c.name || "Sem nome"}</span>
                    <span className="text-slate-400 font-mono">{c.phone}</span>
                  </div>
                ))}
                {audience.total > 10 && (
                  <div className="text-[11px] text-slate-500 pt-1">… + {audience.total - 10} outros</div>
                )}
              </div>
            </div>
          </main>
        </div>

        <footer className="px-6 py-4 border-t border-slate-100 bg-slate-50/70 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="text-[12.5px] font-medium text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-100"
            data-testid="bulk-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !audience.total || message.trim().length < 5}
            data-testid="bulk-send-confirm"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white text-[12.5px] font-semibold hover:bg-slate-800 disabled:opacity-50"
          >
            {sending ? (
              <><ArrowsClockwise size={13} className="animate-spin" /> Enviando…</>
            ) : (
              <><PaperPlaneRight size={13} weight="fill" /> Enviar agora</>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
